import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import { BLOCK_FIELD_NAME_PAIRS } from '../../components/editor/blockFieldNamePairs';

/**
 * WEB-1810 — куда ведёт ОТДЕЛЬНЫЙ вариант ответа (`nextScreenId`, ступень 2
 * лестницы перехода). С решения владельца 22.10.2026 ~22:40 ступень 2 судит и
 * одиночный выбор картинкой (D6 расширено на image_choice, общее множество
 * `OPTION_NAV_BLOCK_TYPES` в `logic-engine/funnel-graph.ts`), поэтому запись
 * идёт в ЖИВОЙ список вариантов блока по реестру пар имён — тот же выбор, что
 * у `resolveEditorBlockOptions`: имя кабинета (`imageChoiceOptions` у выбора
 * картинкой), иначе имя генерации `options`. Прежнее тело в фасаде писало
 * только в `options` и у кабинетного выбора картинкой завело бы рядом второй
 * список, которого плеер не читает.
 *
 * Почему своим действием, а не через `updateBlockOption`: тот пишет ТЕКСТ
 * варианта (label + displayLabel обязательными полями), и подмешивать в него
 * настройку значило бы делать текст необязательным у ВСЕХ его вызывающих.
 * Состав списка действие не меняет, длина массива прежняя.
 *
 * `undefined` — возврат к «следующему по порядку»: ключ УБИРАЕТСЯ, а не
 * остаётся со значением undefined (WEB-955) — иначе очищенный вариант не
 * равен варианту, которого поле не касалось, и отмена/повтор видят правку
 * там, где её нет.
 *
 * Живёт здесь, а не в editorSlice.ts: фасад держит храповик длины (WEB-2552).
 * Создатель действия и регистрация — `./screenFieldActions.ts`, имя прежнее
 * байт в байт: `editor/setBlockOptionNextScreen`.
 */
export interface BlockOptionNextScreenPayload {
  screenId: string;
  blockId: string;
  optionId: string;
  nextScreenId?: string;
}

const GENERATED_OPTIONS_FIELD = 'options';

/** Имя поля живого списка вариантов блока (зеркало resolveEditorBlockOptions). */
function liveOptionsField(block: Record<string, unknown>): string {
  const editorName =
    typeof block.type === 'string'
      ? (BLOCK_FIELD_NAME_PAIRS[block.type]?.[GENERATED_OPTIONS_FIELD] ??
        GENERATED_OPTIONS_FIELD)
      : GENERATED_OPTIONS_FIELD;
  return Array.isArray(block[editorName])
    ? editorName
    : GENERATED_OPTIONS_FIELD;
}

export function setBlockOptionNextScreenReducer(
  state: EditorState,
  action: PayloadAction<BlockOptionNextScreenPayload>,
): EditorState {
  const { screenId, blockId, optionId, nextScreenId } = action.payload;
  let touched = false;
  const content = state.content.map((s) =>
    s.id !== screenId
      ? s
      : {
          ...s,
          blocks: s.blocks.map((b) => {
            if (b.id !== blockId) return b;
            const record = b as unknown as Record<string, unknown>;
            const field = liveOptionsField(record);
            const list = record[field];
            if (!Array.isArray(list)) return b;
            const next = list.map((o: Record<string, unknown>) => {
              if (o?.id !== optionId) return o;
              touched = true;
              if (nextScreenId === undefined) {
                const { nextScreenId: _dropped, ...rest } = o;
                return rest;
              }
              return { ...o, nextScreenId };
            });
            return { ...b, [field]: next } as typeof b;
          }),
        },
  );
  return touched ? { ...state, content, isDirty: true } : state;
}
